/**
 * helpSearchHistory — historique « récents » BICÉPHALE du Help Drawer (§11 + lot §5).
 *
 * Une seule liste, **deux types d'entrées** :
 *   - `search` — requête lexicale rejouable (re-remplit + re-filtre) ;
 *   - `conversation` — fil IA complet (questions + réponses + sources) rouvert
 *     **tel quel, sans nouvel appel IA**, et poursuivable.
 *
 * Stockage localStorage UNIQUEMENT, clé par **tenant + utilisateur**
 * (`argalis-help-searches:{tenant}:{user}`). Le fil peut contenir du texte libre
 * potentiellement nominatif → politique RGPD stricte : suppression individuelle,
 * **purge au logout**, jamais côté serveur.
 */

import type { AssistantTurn } from '../hooks/useHelpAssistant'
import { MAX_RECENT_CONVERSATIONS, MAX_RECENT_SEARCHES } from './helpConstants'

const PREFIX = 'argalis-help-searches'

export interface RecentSearchEntry {
    kind: 'search'
    query: string
}

export interface RecentConversationEntry {
    kind: 'conversation'
    /** Identifiant stable du fil (upsert au fil des tours). */
    id: string
    /** Titre = première question utilisateur (tronquée). */
    title: string
    /** Fil complet rejouable sans régénération. */
    turns: AssistantTurn[]
}

export type RecentEntry = RecentSearchEntry | RecentConversationEntry

/** Clé d'identité d'une entrée (pour dédoublonnage / suppression). */
export function recentEntryKey(entry: RecentEntry): string {
    return entry.kind === 'search'
        ? `s:${entry.query.toLowerCase()}`
        : `c:${entry.id}`
}

/** Identifiant tenant côté front = sous-domaine (même convention que Sentry). */
export function helpTenantSlug(): string {
    if (typeof window === 'undefined') return 'local'
    return window.location.hostname.split('.')[0] || 'local'
}

function storageKey(userId: string): string {
    return `${PREFIX}:${helpTenantSlug()}:${userId}`
}

function coerceEntry(raw: unknown): RecentEntry | null {
    // Format hérité (lot V1) : tableau de chaînes = recherches.
    if (typeof raw === 'string') {
        return raw.trim() ? { kind: 'search', query: raw } : null
    }
    if (raw && typeof raw === 'object') {
        const e = raw as Record<string, unknown>
        if (e.kind === 'search' && typeof e.query === 'string' && e.query.trim()) {
            return { kind: 'search', query: e.query }
        }
        if (
            e.kind === 'conversation' &&
            typeof e.id === 'string' &&
            Array.isArray(e.turns) &&
            e.turns.length > 0
        ) {
            return {
                kind: 'conversation',
                id: e.id,
                title: typeof e.title === 'string' ? e.title : '',
                turns: e.turns as AssistantTurn[],
            }
        }
    }
    return null
}

export function loadRecents(userId: string | undefined): RecentEntry[] {
    if (typeof window === 'undefined' || !userId) return []
    try {
        const raw = window.localStorage.getItem(storageKey(userId))
        if (!raw) return []
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        return parsed.map(coerceEntry).filter((e): e is RecentEntry => e !== null)
    } catch {
        return []
    }
}

function persist(userId: string, entries: RecentEntry[]): void {
    try {
        window.localStorage.setItem(storageKey(userId), JSON.stringify(entries))
    } catch {
        /* localStorage indisponible ou quota dépassé : on ignore silencieusement */
    }
}

/** Applique le plafond PAR TYPE en préservant l'ordre (le plus récent en tête). */
function capPerType(entries: RecentEntry[]): RecentEntry[] {
    let searches = 0
    let conversations = 0
    const out: RecentEntry[] = []
    for (const e of entries) {
        if (e.kind === 'search') {
            if (searches < MAX_RECENT_SEARCHES) {
                out.push(e)
                searches++
            }
        } else if (conversations < MAX_RECENT_CONVERSATIONS) {
            out.push(e)
            conversations++
        }
    }
    return out
}

/** Mémorise une recherche VALIDÉE (article ouvert depuis la recherche). */
export function recordSearch(userId: string | undefined, query: string): RecentEntry[] {
    const q = query.trim()
    if (typeof window === 'undefined' || !userId || !q) return loadRecents(userId)
    const existing = loadRecents(userId).filter(
        (e) => !(e.kind === 'search' && e.query.toLowerCase() === q.toLowerCase()),
    )
    const next = capPerType([{ kind: 'search', query: q }, ...existing])
    persist(userId, next)
    return next
}

/** Upsert d'une conversation IA (au fil des tours, par `id`). */
export function recordConversation(
    userId: string | undefined,
    id: string,
    turns: AssistantTurn[],
    title: string,
): RecentEntry[] {
    if (typeof window === 'undefined' || !userId || !id || turns.length === 0) {
        return loadRecents(userId)
    }
    const existing = loadRecents(userId).filter(
        (e) => !(e.kind === 'conversation' && e.id === id),
    )
    const next = capPerType([{ kind: 'conversation', id, title, turns }, ...existing])
    persist(userId, next)
    return next
}

/** Suppression individuelle immédiate et effective. */
export function removeRecent(userId: string | undefined, entry: RecentEntry): RecentEntry[] {
    if (typeof window === 'undefined' || !userId) return []
    const key = recentEntryKey(entry)
    const next = loadRecents(userId).filter((e) => recentEntryKey(e) !== key)
    persist(userId, next)
    return next
}

/** Efface tout l'historique de l'utilisateur courant (tenant courant). */
export function clearRecents(userId: string | undefined): void {
    if (typeof window === 'undefined' || !userId) return
    try {
        window.localStorage.removeItem(storageKey(userId))
    } catch {
        /* noop */
    }
}

/**
 * Purge TOUTES les clés d'historique d'aide de ce navigateur (tous tenants/users).
 * Appelée au logout : ne laisse aucune requête ni fil derrière soi.
 */
export function purgeAllHelpSearches(): void {
    if (typeof window === 'undefined') return
    try {
        const toRemove: string[] = []
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i)
            if (key && key.startsWith(`${PREFIX}:`)) toRemove.push(key)
        }
        for (const key of toRemove) window.localStorage.removeItem(key)
    } catch {
        /* noop */
    }
}
